// Preferred-language ranking for player subtitle + audio track lists
// (CLAUDE-TODO #343). Pure ESM, no Vue/@ imports → node --test'able.
//
// A user sets a primary + secondary preferred language in Settings; these
// helpers push tracks in those languages to the top of a list and pick the
// best audio track to pre-select. Track language codes come from several
// sources (Put.io subtitles, ffprobe streams, HLS manifests) in mixed
// shapes — ISO 639-1 ('en'), ISO 639-2 ('eng'/'fre'), or locale tags
// ('pt-BR') — so everything is normalized to a 2-letter base first.

// ISO 639-2/B and /T three-letter codes → 639-1 two-letter, for the
// languages that realistically show up in media subtitle/audio streams.
const ISO_639_2_TO_1 = {
  eng: "en",
  fre: "fr",
  fra: "fr",
  spa: "es",
  ger: "de",
  deu: "de",
  ita: "it",
  por: "pt",
  rus: "ru",
  jpn: "ja",
  kor: "ko",
  chi: "zh",
  zho: "zh",
  ara: "ar",
  hin: "hi",
  dut: "nl",
  nld: "nl",
  swe: "sv",
  nor: "no",
  dan: "da",
  fin: "fi",
  pol: "pl",
  tur: "tr",
  ces: "cs",
  cze: "cs",
  ell: "el",
  gre: "el",
  heb: "he",
  tha: "th",
  vie: "vi",
  ind: "id",
  ukr: "uk",
  ron: "ro",
  rum: "ro",
  hun: "hu",
};

// Normalize a raw language code to a lowercase 2-letter base, or '' when it
// carries no usable language ('und', empty, null).
export function normalizeLang(code) {
  if (typeof code !== "string") return "";
  let c = code.trim().toLowerCase();
  if (!c) return "";
  // Collapse locale tags: 'pt-BR' / 'en_US' → 'pt' / 'en'.
  c = c.split(/[-_]/)[0];
  if (c === "und" || c === "mis" || c === "zxx") return "";
  if (c.length === 3 && ISO_639_2_TO_1[c]) return ISO_639_2_TO_1[c];
  return c;
}

// Build a normalized, de-duped preferred-language list (primary first).
function normalizePreferred(preferred) {
  if (!Array.isArray(preferred)) return [];
  const out = [];
  const seen = new Set();
  for (const p of preferred) {
    const n = normalizeLang(p);
    if (!n || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

// Position of `code`'s language in the preferred list (0 = primary), or
// +Infinity when it isn't a preferred language.
export function preferredRank(code, preferred) {
  const list = normalizePreferred(preferred);
  if (list.length === 0) return Number.POSITIVE_INFINITY;
  const n = normalizeLang(code);
  if (!n) return Number.POSITIVE_INFINITY;
  const idx = list.indexOf(n);
  return idx === -1 ? Number.POSITIVE_INFINITY : idx;
}

// Stable sort that floats preferred-language items to the top while keeping
// the caller's existing order within each rank bucket (so e.g. Put.io's
// filename-match scoring is preserved as the tie-break). Returns a new array.
export function sortByPreferredLanguage(items, getCode, preferred) {
  const list = normalizePreferred(preferred);
  if (!Array.isArray(items) || items.length === 0 || list.length === 0) {
    return Array.isArray(items) ? items.slice() : [];
  }
  return items
    .map((item, index) => ({
      item,
      index,
      rank: preferredRank(getCode(item), list),
    }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((e) => e.item);
}

// Curated language list for the Settings preferred-language pickers. ISO
// 639-1 code + English name. Covers the languages that realistically appear
// as media audio/subtitle tracks; ordered roughly by global media prevalence.
export const COMMON_LANGUAGE_OPTIONS = [
  { code: "en", name: "English" },
  { code: "es", name: "Spanish" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" },
  { code: "it", name: "Italian" },
  { code: "pt", name: "Portuguese" },
  { code: "ru", name: "Russian" },
  { code: "ja", name: "Japanese" },
  { code: "ko", name: "Korean" },
  { code: "zh", name: "Chinese" },
  { code: "ar", name: "Arabic" },
  { code: "hi", name: "Hindi" },
  { code: "nl", name: "Dutch" },
  { code: "sv", name: "Swedish" },
  { code: "no", name: "Norwegian" },
  { code: "da", name: "Danish" },
  { code: "fi", name: "Finnish" },
  { code: "pl", name: "Polish" },
  { code: "tr", name: "Turkish" },
  { code: "cs", name: "Czech" },
  { code: "el", name: "Greek" },
  { code: "he", name: "Hebrew" },
  { code: "th", name: "Thai" },
  { code: "vi", name: "Vietnamese" },
  { code: "id", name: "Indonesian" },
  { code: "uk", name: "Ukrainian" },
  { code: "ro", name: "Romanian" },
  { code: "hu", name: "Hungarian" },
];

// Index of the track best matching the preferred languages: the first track
// in the primary language, else the first in the secondary (and so on down
// the list), else -1. Used to pre-select an audio track on load.
export function pickPreferredIndex(items, getCode, preferred) {
  const list = normalizePreferred(preferred);
  if (!Array.isArray(items) || items.length === 0 || list.length === 0)
    return -1;
  for (const lang of list) {
    const idx = items.findIndex(
      (item) => normalizeLang(getCode(item)) === lang,
    );
    if (idx !== -1) return idx;
  }
  return -1;
}
